import React, { useState, useEffect } from 'react';
import { Modal, Form, Button, Alert, Nav, Tab, Row, Col, Table } from 'react-bootstrap';
import { useAuth } from '../context/AuthContext';
import { apiClient } from '../utils/apiUtils';
import { useNotification } from '../hooks/useNotification';

const MonitoringToolModal = ({ show, onHide, monitoringId, onSuccess }) => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('project');
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const { notification, showSuccess, showError, dismiss } = useNotification();
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [wasNewMonitoring, setWasNewMonitoring] = useState(false);

  // Form state
  const [selectedProject, setSelectedProject] = useState(null);
  const [projects, setProjects] = useState([]);
  const [projectDetails, setProjectDetails] = useState({
    date: '',
    extensionProgramTitle: '',
    proponents: '',
    totalProjectCost: '',
    duration: '',
    totalAccomplishmentDate: ''
  });
  const [trackingNumber, setTrackingNumber] = useState('');
  // Helper function to create initial work plan rows (10 rows by default)
  const createInitialWorkPlanRows = () => {
    return Array.from({ length: 10 }, (_, index) => ({
      id: index + 1,
      activities: '',
      percentWeight: '',
      physicalTargets: '',
      targetDate: '',
      actualAccomplishment: '',
      percentageAccomplished: '',
      problemsChallenges: ''
    }));
  };

  const [workPlanRows, setWorkPlanRows] = useState(createInitialWorkPlanRows());
  const [budgetRows, setBudgetRows] = useState([
    {
      id: 1,
      type: 'Purchase Request',
      details: '',
      amountRequested: '',
      actualAmount: '',
      budgetUtilized: '',
      remarks: ''
    },
    {
      id: 2,
      type: 'Cash Advance',
      details: '',
      amountRequested: '',
      actualAmount: '',
      budgetUtilized: '',
      remarks: ''
    },
    {
      id: 3,
      type: 'Request of Payment',
      details: '',
      amountRequested: '',
      actualAmount: '',
      budgetUtilized: '',
      remarks: ''
    }
  ]);
  const [budgetTotals, setBudgetTotals] = useState({
    totalAmountRequested: '',
    totalActualAmount: '',
    totalBudgetUtilized: ''
  });
  const [monitoringStatus, setMonitoringStatus] = useState('Pending');
  const [selectedSignature, setSelectedSignature] = useState(null);
  const [signaturePreview, setSignaturePreview] = useState(null);
  const [existingSignaturePath, setExistingSignaturePath] = useState(null);
  const [signatoryPrefs, setSignatoryPrefs] = useState({
    prepared_by_enabled: true,
    monitored_by_enabled: true,
    reviewed_by_enabled: true
  });

  const isEditMode = !!monitoringId;
  const canEditStatus = user && user.role === 'Admin';

  useEffect(() => {
    if (show) {
      fetchProjects();
      if (isEditMode) {
        loadMonitoringTool();
      } else {
        resetForm();
      }
    }
  }, [show, isEditMode, monitoringId]);

  const resetForm = () => {
    setSelectedProject(null);
    setProjectDetails({
      date: '',
      extensionProgramTitle: '',
      proponents: '',
      totalProjectCost: '',
      duration: '',
      totalAccomplishmentDate: ''
    });
    setTrackingNumber('');
    setWorkPlanRows(createInitialWorkPlanRows());
    setSelectedSignature(null);
    setSignaturePreview(null);
    setExistingSignaturePath(null);
    // Reset file input
    const fileInput = document.getElementById('monitoring-signature-upload');
    if (fileInput) fileInput.value = '';
    setBudgetRows([
      {
        id: 1,
        type: 'Purchase Request',
        details: '',
        amountRequested: '',
        actualAmount: '',
        budgetUtilized: '',
        remarks: ''
      },
      {
        id: 2,
        type: 'Cash Advance',
        details: '',
        amountRequested: '',
        actualAmount: '',
        budgetUtilized: '',
        remarks: ''
      },
      {
        id: 3,
        type: 'Request of Payment',
        details: '',
        amountRequested: '',
        actualAmount: '',
        budgetUtilized: '',
        remarks: ''
      }
    ]);
    setBudgetTotals({
      totalAmountRequested: '',
      totalActualAmount: '',
      totalBudgetUtilized: ''
    });
    setMonitoringStatus('Pending');
    setSignatoryPrefs({
      prepared_by_enabled: true,
      monitored_by_enabled: true,
      reviewed_by_enabled: true
    });
    setActiveTab('project');
    dismiss();
  };

  const fetchProjects = async () => {
    try {
      setLoading(true);
      const { data } = await apiClient.get('/projects/monitoring');
      setProjects(data);
    } catch (err) {
      console.error('Failed to load projects', err);
      setProjects([]);
    } finally {
      setLoading(false);
    }
  };

  const loadMonitoringTool = async () => {
    try {
      setLoading(true);
      const { data } = await apiClient.get(`/monitoring/${monitoringId}`);
      
      // Wait for projects to be loaded if they're not already
      if (projects.length === 0) {
        await fetchProjects();
      }
      
      // Set the selected project
      let project = projects.find(p => p.project_id === data.project_id);
      
      if (!project && data.project_id) {
        try {
          const { data: projectData } = await apiClient.get(`/projects/${data.project_id}`);
          project = projectData;
        } catch (projectErr) {
          console.error('Failed to fetch specific project:', projectErr);
        }
      }
      
      if (project) {
        setSelectedProject(project);
      }
      
      // Populate project details
      const dateObj = data.date ? new Date(data.date) : null;
      let formattedDate = '';
      if (dateObj) {
        const year = dateObj.getFullYear();
        const month = (dateObj.getMonth() + 1).toString().padStart(2, '0');
        const day = dateObj.getDate().toString().padStart(2, '0');
        formattedDate = `${year}-${month}-${day}`;
      }
      
      // Format total accomplishment date if it exists
      const accomplishmentDateObj = data.total_accomplishment_date ? new Date(data.total_accomplishment_date) : null;
      let formattedAccomplishmentDate = '';
      if (accomplishmentDateObj) {
        const year = accomplishmentDateObj.getFullYear();
        const month = (accomplishmentDateObj.getMonth() + 1).toString().padStart(2, '0');
        const day = accomplishmentDateObj.getDate().toString().padStart(2, '0');
        formattedAccomplishmentDate = `${year}-${month}-${day}`;
      }
      
      setProjectDetails({
        date: formattedDate,
        extensionProgramTitle: data.extension_program_title,
        proponents: data.proponents,
        totalProjectCost: data.total_project_cost?.toString() || '',
        duration: data.duration || '',
        totalAccomplishmentDate: formattedAccomplishmentDate
      });
      setTrackingNumber(data.tracking_number || '');
      
      setMonitoringStatus(data.status || 'Pending');

      // Load signatory prefs (default to enabled)
      setSignatoryPrefs({
        prepared_by_enabled: Boolean(Number(data.prepared_by_enabled ?? 1)),
        monitored_by_enabled: Boolean(Number(data.monitored_by_enabled ?? 1)),
        reviewed_by_enabled: Boolean(Number(data.reviewed_by_enabled ?? 1))
      });
      
      // Load existing signature if available
      if (data.photo_path) {
        setExistingSignaturePath(data.photo_path);
        setSignaturePreview(null); // Will be loaded from URL if needed
      } else {
        setExistingSignaturePath(null);
        setSignaturePreview(null);
      }
      
      // Populate work plan data
      if (data.work_plan && Array.isArray(data.work_plan)) {
        setWorkPlanRows(data.work_plan);
      }
      
      // Populate budget utilization data
      const defaultBudgetRows = [
        {
          id: 1,
          type: 'Purchase Request',
          details: '',
          amountRequested: '',
          actualAmount: '',
          budgetUtilized: '',
          remarks: ''
        },
        {
          id: 2,
          type: 'Cash Advance',
          details: '',
          amountRequested: '',
          actualAmount: '',
          budgetUtilized: '',
          remarks: ''
        },
        {
          id: 3,
          type: 'Request of Payment',
          details: '',
          amountRequested: '',
          actualAmount: '',
          budgetUtilized: '',
          remarks: ''
        }
      ];

      if (data.budget_utilization && Array.isArray(data.budget_utilization)) {
        const mergedRows = defaultBudgetRows.map(defaultRow => {
          const existingRow = data.budget_utilization.find(row => row.type === defaultRow.type);
          return existingRow ? { ...defaultRow, ...existingRow } : defaultRow;
        });
        setBudgetRows(mergedRows);
      } else {
        setBudgetRows(defaultBudgetRows);
      }

      if (data.budget_totals) {
        setBudgetTotals({
          totalAmountRequested: data.budget_totals.totalAmountRequested || '',
          totalActualAmount: data.budget_totals.totalActualAmount || '',
          totalBudgetUtilized: data.budget_totals.totalBudgetUtilized || ''
        });
      }
    } catch (err) {
      console.error('Failed to load monitoring tool:', err);
      showError('Failed to load monitoring tool data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleProjectChange = async (projectId) => {
    setSelectedProject(null);
    
    if (!projectId) {
      setProjectDetails({
        date: '',
        extensionProgramTitle: '',
        proponents: '',
        totalProjectCost: '',
        duration: '',
        totalAccomplishmentDate: ''
      });
      return;
    }
    
    const project = projects.find(p => p.project_id == projectId);
    setSelectedProject(project);
    
    try {
      const { data: fullProjectData } = await apiClient.get(`/projects/${projectId}`);
      setSelectedProject(fullProjectData);
      
      setProjectDetails(prev => ({
        ...prev,
        extensionProgramTitle: fullProjectData.title,
        proponents: fullProjectData.project_leader_name
      }));
    } catch (err) {
      console.error('Failed to load full project details', err);
      if (project) {
        setProjectDetails(prev => ({
          ...prev,
          extensionProgramTitle: project.title,
          proponents: project.project_leader_name
        }));
      }
    }
  };

  const handleProjectDetailsChange = (field, value) => {
    if (field === 'totalProjectCost') {
      const formattedValue = value.replace(/[^0-9.,]/g, '');
      setProjectDetails(prev => ({
        ...prev,
        [field]: formattedValue
      }));
    } else {
      setProjectDetails(prev => ({
        ...prev,
        [field]: value
      }));
    }
  };

  const handleWorkPlanChange = (rowId, field, value) => {
    setWorkPlanRows(prev => 
      prev.map(row => 
        row.id === rowId 
          ? { ...row, [field]: value }
          : row
      )
    );
  };

  const handleBudgetChange = (rowId, field, value) => {
    setBudgetRows(prev => 
      prev.map(row => 
        row.id === rowId 
          ? { ...row, [field]: value }
          : row
      )
    );
  };

  const addBudgetRow = () => {
    const newId = Math.max(...budgetRows.map(row => row.id)) + 1;
    setBudgetRows(prev => [...prev, {
      id: newId,
      type: '',
      details: '',
      amountRequested: '',
      actualAmount: '',
      budgetUtilized: '',
      remarks: ''
    }]);
  };

  const removeBudgetRow = () => {
    if (budgetRows.length > 3) {
      setBudgetRows(prev => prev.slice(0, -1));
    }
  };

  const handleSignatureSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      showError('Please select an image file (JPG, PNG, GIF, or WebP)');
      return;
    }

    // Validate file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      showError('Image size must be less than 10MB');
      return;
    }

    setSelectedSignature(file);
    setExistingSignaturePath(null); // Clear existing signature when new one is selected

    // Create preview
    const reader = new FileReader();
    reader.onloadend = () => {
      setSignaturePreview(reader.result);
    };
    reader.readAsDataURL(file);
  };

  const handleRemoveSignature = () => {
    setSelectedSignature(null);
    setSignaturePreview(null);
    setExistingSignaturePath(null);
    // Reset file input
    const fileInput = document.getElementById('monitoring-signature-upload');
    if (fileInput) fileInput.value = '';
  };

  const handleBudgetTotalsChange = (field, value) => {
    setBudgetTotals(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const addWorkPlanRow = () => {
    const newId = Math.max(...workPlanRows.map(row => row.id)) + 1;
    setWorkPlanRows(prev => [...prev, {
      id: newId,
      activities: '',
      percentWeight: '',
      physicalTargets: '',
      targetDate: '',
      actualAccomplishment: '',
      percentageAccomplished: '',
      problemsChallenges: ''
    }]);
  };

  const removeWorkPlanRow = () => {
    if (workPlanRows.length > 1) {
      setWorkPlanRows(prev => prev.slice(0, -1));
    }
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    dismiss();

    try {
      // If signature is selected, use FormData; otherwise use JSON
      const hasSignature = selectedSignature !== null;
      
      let formData;
      if (hasSignature) {
        formData = new FormData();
        formData.append('project_id', selectedProject?.project_id || '');
        formData.append('tracking_number', trackingNumber || '');
        formData.append('date', projectDetails.date);
        formData.append('extensionProgramTitle', projectDetails.extensionProgramTitle);
        formData.append('proponents', projectDetails.proponents);
        formData.append('totalProjectCost', projectDetails.totalProjectCost ? parseFloat(projectDetails.totalProjectCost.replace(/,/g, '')) : 0);
        formData.append('duration', projectDetails.duration);
        formData.append('totalAccomplishmentDate', projectDetails.totalAccomplishmentDate || '');
        formData.append('workPlan', JSON.stringify(workPlanRows));
        formData.append('budgetUtilization', JSON.stringify(budgetRows));
        formData.append('budgetTotals', JSON.stringify(budgetTotals));
        formData.append('status', monitoringStatus);
        formData.append('signatory_prefs', JSON.stringify(signatoryPrefs));
        formData.append('photo', selectedSignature); // Backend still expects 'photo' field name
      } else {
        formData = {
          project_id: selectedProject?.project_id || null,
          tracking_number: trackingNumber || null,
          date: projectDetails.date,
          extensionProgramTitle: projectDetails.extensionProgramTitle,
          proponents: projectDetails.proponents,
          totalProjectCost: projectDetails.totalProjectCost ? parseFloat(projectDetails.totalProjectCost.replace(/,/g, '')) : 0,
          duration: projectDetails.duration,
          totalAccomplishmentDate: projectDetails.totalAccomplishmentDate || null,
          workPlan: workPlanRows,
          budgetUtilization: budgetRows,
          budgetTotals: budgetTotals,
          status: monitoringStatus,
          signatory_prefs: signatoryPrefs
        };
      }

      let response;
      if (isEditMode) {
        response = await apiClient.put(`/monitoring/${monitoringId}`, formData);
        setWasNewMonitoring(false);
        showSuccess('Monitoring Tool updated successfully!');
      } else {
        response = await apiClient.post('/monitoring', formData);
        setWasNewMonitoring(true);
        showSuccess('Monitoring Tool created successfully!');
      }
      
      setShowSuccessModal(true);
      if (onSuccess) {
        onSuccess();
      }
    } catch (err) {
      console.error(`Error ${isEditMode ? 'updating' : 'creating'} monitoring tool:`, err);
      if (err.response?.data?.errors) {
        showError(`Validation error: ${err.response.data.errors.map(e => e.msg).join(', ')}`);
      } else if (err.response?.data?.error) {
        showError(`Server error: ${err.response.data.error}`);
      } else {
        showError(`Failed to ${isEditMode ? 'update' : 'create'} monitoring tool. Please try again.`);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    resetForm();
    dismiss();
    onHide();
  };

  const handleNextTab = () => {
    if (activeTab === 'project') {
      setActiveTab('workplan');
    } else if (activeTab === 'workplan') {
      setActiveTab('budget');
    } else if (activeTab === 'budget') {
      setActiveTab('signatories');
    }
  };

  const handlePreviousTab = () => {
    if (activeTab === 'signatories') {
      setActiveTab('budget');
    } else if (activeTab === 'budget') {
      setActiveTab('workplan');
    } else if (activeTab === 'workplan') {
      setActiveTab('project');
    }
  };

  const toggleSignatoryPref = (key) => {
    setSignatoryPrefs(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  const renderSignatoriesTab = () => {
    const disabled = loading || submitting;
    const signatoryOptions = [
      { key: 'prepared_by_enabled', id: 'mt-signatory-prepared-by', label: 'Prepared by (Project Leader)' },
      { key: 'monitored_by_enabled', id: 'mt-signatory-monitored-by', label: 'Monitored by (Extension Head)' },
      { key: 'reviewed_by_enabled', id: 'mt-signatory-reviewed-by', label: 'Reviewed & Evaluated by (VC-RDES)' }
    ];

    return (
      <div style={{ maxHeight: '60vh', overflowY: 'auto', padding: '1rem' }}>
        <Alert variant="info" className="mb-3">
          <i className="bi bi-info-circle me-2"></i>
          Enable/disable which signatory sections will appear in the generated DOCX. Disabling a signatory will output no
          name/designation/date/signature for that section.
        </Alert>

        {signatoryOptions.map((opt) => (
          <div
            key={opt.key}
            role="checkbox"
            aria-checked={!!signatoryPrefs[opt.key]}
            tabIndex={0}
            className="d-flex align-items-center gap-2 mb-3"
            style={{
              cursor: disabled ? 'not-allowed' : 'pointer',
              userSelect: 'none',
              opacity: disabled ? 0.6 : 1
            }}
            onClick={() => {
              if (disabled) return;
              toggleSignatoryPref(opt.key);
            }}
            onKeyDown={(e) => {
              if (disabled) return;
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                toggleSignatoryPref(opt.key);
              }
            }}
          >
            <input
              id={opt.id}
              type="checkbox"
              checked={!!signatoryPrefs[opt.key]}
              readOnly
              disabled={disabled}
              style={{
                pointerEvents: 'none', // ensure ONE click path (container) toggles once
                marginTop: 2
              }}
            />
            <label
              htmlFor={opt.id}
              className="mb-0"
              style={{ cursor: disabled ? 'not-allowed' : 'pointer', marginLeft: 4 }}
            >
              {opt.label}
            </label>
          </div>
        ))}
      </div>
    );
  };

  const renderProjectTab = () => (
    <div>
      {/* Project Selection - Only show when creating new monitoring tool */}
      {!isEditMode && (
        <div className="mb-4">
          <h6 className="mb-3">Select Project</h6>
          <Form.Group className="mb-3">
            <Form.Label className="fw-semibold">Select Project (Approved by Chancellor) for Monitoring Tool</Form.Label>
            <Form.Select
              value={selectedProject?.project_id || ''}
              onChange={(e) => handleProjectChange(e.target.value)}
              disabled={loading}
              style={{ border: '1px solid #ced4da', padding: '10px' }}
            >
              <option value="">
                {loading ? 'Loading projects...' : 'Choose an approved project...'}
              </option>
              {projects.map(project => (
                <option key={project.project_id} value={project.project_id}>
                  {project.title}
                </option>
              ))}
            </Form.Select>
            <Form.Text className="text-muted">
              Only projects approved by the Chancellor are available for monitoring tools
              {projects.length > 0 && (
                <span className="ms-2">
                  ({projects.length} approved projects available)
                </span>
              )}
            </Form.Text>
            {!loading && projects.length === 0 && (
              <Alert variant="warning" className="mt-2">
                <i className="bi bi-exclamation-triangle me-2"></i>
                No approved projects available. Projects must be marked as "Approved by Chancellor" before monitoring tools can be created.
              </Alert>
            )}
          </Form.Group>
        </div>
      )}

      {/* Tracking Number - below project selection */}
      <div className="mb-4">
        <Form.Group>
          <Form.Label className="fw-semibold">Tracking Number</Form.Label>
          <Form.Control
            type="text"
            value={trackingNumber}
            onChange={(e) => setTrackingNumber(e.target.value)}
            style={{ border: '1px solid #ced4da', padding: '10px' }}
          />
          <Form.Text className="text-muted">
            This value will appear in the footer as the Tracking No. for this Monitoring Tool.
          </Form.Text>
        </Form.Group>
      </div>

      {/* Status Field - Admin Only - Edit Mode Only */}
      {canEditStatus && isEditMode && (
        <div className="mb-4">
          <Form.Group>
            <Form.Label className="fw-semibold">Status:</Form.Label>
            <Form.Select
              value={monitoringStatus}
              onChange={(e) => setMonitoringStatus(e.target.value)}
              style={{ border: '1px solid #ced4da', padding: '10px' }}
            >
              <option value="Pending">Pending</option>
              <option value="Approved by Extension Head">Approved by Extension Head</option>
              <option value="Rejected by Extension Head">Rejected by Extension Head</option>
              <option value="Approved by VC-RDES">Approved by VC-RDES</option>
              <option value="Rejected by VC-RDES">Rejected by VC-RDES</option>
            </Form.Select>
            <Form.Text className="text-muted">Only Admin can change monitoring tool status</Form.Text>
          </Form.Group>
        </div>
      )}

      <hr className="my-3" style={{ borderTop: '1px solid #dee2e6' }} />

      {/* Project Details - Show when project selected or in edit mode */}
      {(selectedProject || isEditMode) && (
        <div>
          <h6 className="mb-3">Project Details</h6>
          <hr className="my-3" style={{ borderTop: '1px solid #dee2e6' }} />
          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label className="fw-semibold">Date</Form.Label>
                <Form.Control
                  type="date"
                  value={projectDetails.date}
                  onChange={(e) => handleProjectDetailsChange('date', e.target.value)}
                  style={{ border: '1px solid #ced4da', padding: '10px' }}
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label className="fw-semibold">Total Project Cost</Form.Label>
                <Form.Control
                  type="text"
                  value={projectDetails.totalProjectCost}
                  onChange={(e) => handleProjectDetailsChange('totalProjectCost', e.target.value)}
                  placeholder="Enter total project cost (e.g., 1,000,000.00)"
                  style={{ border: '1px solid #ced4da', padding: '10px' }}
                />
              </Form.Group>
            </Col>
          </Row>
          <hr className="my-3" style={{ borderTop: '1px solid #dee2e6' }} />
          <Row>
            <Col md={12}>
              <Form.Group className="mb-3">
                <Form.Label className="fw-semibold">Project Title</Form.Label>
                <Form.Control
                  type="text"
                  value={projectDetails.extensionProgramTitle}
                  readOnly
                  className="bg-light"
                  style={{ border: '1px solid #ced4da', padding: '10px' }}
                />
                <Form.Text className="text-muted">
                  This field is automatically populated from the selected project
                </Form.Text>
              </Form.Group>
            </Col>
          </Row>
          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label className="fw-semibold">Name of Proponents</Form.Label>
                <Form.Control
                  type="text"
                  value={projectDetails.proponents}
                  readOnly
                  className="bg-light"
                  style={{ border: '1px solid #ced4da', padding: '10px' }}
                />
                <Form.Text className="text-muted">
                  This field is automatically populated from the selected project
                </Form.Text>
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label className="fw-semibold">Duration (Approved Start & End of Implementation)</Form.Label>
                <Form.Control
                  type="text"
                  value={projectDetails.duration}
                  onChange={(e) => handleProjectDetailsChange('duration', e.target.value)}
                  placeholder="e.g., January 2024 - December 2024"
                  style={{ border: '1px solid #ced4da', padding: '10px' }}
                />
              </Form.Group>
            </Col>
          </Row>
          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label className="fw-semibold">Total Accomplishment Date</Form.Label>
                <Form.Control
                  type="date"
                  value={projectDetails.totalAccomplishmentDate}
                  onChange={(e) => handleProjectDetailsChange('totalAccomplishmentDate', e.target.value)}
                  style={{ border: '1px solid #ced4da', padding: '10px' }}
                />
                <Form.Text className="text-muted">
                  Date for "Total accomplishment as of" in the document
                </Form.Text>
              </Form.Group>
            </Col>
          </Row>
          <Row>
            <Col>
              <Form.Group className="mb-3">
                <Form.Label className="fw-semibold">Upload Signature</Form.Label>
                <Form.Control
                  id="monitoring-signature-upload"
                  type="file"
                  accept="image/*"
                  onChange={handleSignatureSelect}
                  style={{ border: '1px solid #ced4da', padding: '10px' }}
                />
                <Form.Text className="text-muted">
                  Upload your signature for the "Prepared by" section (JPG, PNG, GIF, WebP - Max 10MB)
                </Form.Text>
                {(signaturePreview || existingSignaturePath) && (
                  <div className="mt-2">
                    <div className="d-flex align-items-center gap-2">
                      {signaturePreview ? (
                        <img 
                          src={signaturePreview} 
                          alt="Signature Preview" 
                          style={{ maxWidth: '200px', maxHeight: '200px', objectFit: 'contain', border: '1px solid #ddd', borderRadius: '4px' }}
                        />
                      ) : existingSignaturePath ? (
                        <img 
                          src={existingSignaturePath.startsWith('http') ? existingSignaturePath : `${process.env.REACT_APP_API_URL?.replace('/api', '') || 'http://localhost:5000'}${existingSignaturePath}`}
                          alt="Current signature" 
                          style={{ maxWidth: '200px', maxHeight: '200px', objectFit: 'contain', border: '1px solid #ddd', borderRadius: '4px' }}
                          onError={(e) => {
                            e.target.style.display = 'none';
                          }}
                        />
                      ) : null}
                      <Button 
                        variant="outline-danger" 
                        size="sm" 
                        onClick={handleRemoveSignature}
                        type="button"
                      >
                        <i className="bi bi-trash me-1"></i>
                        Remove
                      </Button>
                    </div>
                  </div>
                )}
              </Form.Group>
            </Col>
          </Row>
        </div>
      )}
    </div>
  );

  const renderWorkPlanTab = () => (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h6 className="mb-0">A. Work Plan</h6>
        <div className="d-flex gap-2">
          <Button variant="link" className="p-0" style={{ color: '#0d6efd' }} onClick={addWorkPlanRow} title="Add row">
            <i className="bi bi-plus-circle-fill" style={{ fontSize: 18 }}></i>
          </Button>
          <Button variant="link" className="p-0" style={{ color: '#d32f2f' }} onClick={removeWorkPlanRow} title="Remove last row" disabled={workPlanRows.length <= 1}>
            <i className="bi bi-dash-circle-fill" style={{ fontSize: 18 }}></i>
          </Button>
        </div>
      </div>
      <div className="table-responsive">
        <Table striped bordered hover size="sm">
          <thead>
            <tr>
              <th style={{ width: '25%', fontWeight: 'normal' }}>Activities</th>
              <th style={{ width: '10%', fontWeight: 'normal' }}>Percent Weight</th>
              <th style={{ width: '20%', fontWeight: 'normal' }}>Physical Targets</th>
              <th style={{ width: '15%', fontWeight: 'normal' }}>Target Date</th>
              <th style={{ width: '20%', fontWeight: 'normal' }}>Actual Accomplishment</th>
              <th style={{ width: '10%', fontWeight: 'normal' }}>% Accomplished</th>
              <th style={{ width: '15%', fontWeight: 'normal' }}>Problems/Challenges</th>
            </tr>
          </thead>
          <tbody>
            {workPlanRows.map((row, index) => (
              <tr key={row.id}>
                <td style={{ padding: '8px', border: '1px solid #dee2e6' }}>
                  <Form.Control
                    as="textarea"
                    rows={3}
                    value={row.activities}
                    onChange={(e) => handleWorkPlanChange(row.id, 'activities', e.target.value)}
                    placeholder={index === 0 ? "Mention all activities..." : ""}
                    style={{ border: '1px solid #ced4da', padding: '8px', fontSize: '0.875rem' }}
                  />
                </td>
                <td style={{ padding: '8px', border: '1px solid #dee2e6' }}>
                  <Form.Control
                    type="number"
                    value={row.percentWeight}
                    onChange={(e) => handleWorkPlanChange(row.id, 'percentWeight', e.target.value)}
                    placeholder="%"
                    style={{ border: '1px solid #ced4da', padding: '8px' }}
                  />
                </td>
                <td style={{ padding: '8px', border: '1px solid #dee2e6' }}>
                  <Form.Control
                    as="textarea"
                    rows={2}
                    value={row.physicalTargets}
                    onChange={(e) => handleWorkPlanChange(row.id, 'physicalTargets', e.target.value)}
                    placeholder="Target"
                    style={{ border: '1px solid #ced4da', padding: '8px', fontSize: '0.875rem' }}
                  />
                </td>
                <td style={{ padding: '8px', border: '1px solid #dee2e6' }}>
                  <Form.Control
                    as="textarea"
                    rows={2}
                    value={row.targetDate}
                    onChange={(e) => handleWorkPlanChange(row.id, 'targetDate', e.target.value)}
                    placeholder="Target date"
                    style={{ border: '1px solid #ced4da', padding: '8px', fontSize: '0.875rem' }}
                  />
                </td>
                <td style={{ padding: '8px', border: '1px solid #dee2e6' }}>
                  <Form.Control
                    as="textarea"
                    rows={2}
                    value={row.actualAccomplishment}
                    onChange={(e) => handleWorkPlanChange(row.id, 'actualAccomplishment', e.target.value)}
                    placeholder="Actual"
                    style={{ border: '1px solid #ced4da', padding: '8px', fontSize: '0.875rem' }}
                  />
                </td>
                <td style={{ padding: '8px', border: '1px solid #dee2e6' }}>
                  <Form.Control
                    type="number"
                    value={row.percentageAccomplished}
                    onChange={(e) => handleWorkPlanChange(row.id, 'percentageAccomplished', e.target.value)}
                    placeholder="%"
                    min="0"
                    max="100"
                    style={{ border: '1px solid #ced4da', padding: '8px' }}
                  />
                </td>
                <td style={{ padding: '8px', border: '1px solid #dee2e6' }}>
                  <Form.Control
                    as="textarea"
                    rows={2}
                    value={row.problemsChallenges}
                    onChange={(e) => handleWorkPlanChange(row.id, 'problemsChallenges', e.target.value)}
                    placeholder="Challenges"
                    style={{ border: '1px solid #ced4da', padding: '8px', fontSize: '0.875rem' }}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      </div>
    </div>
  );

  const renderBudgetTab = () => (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h6 className="mb-0">B. Budget Utilization</h6>
        <div className="d-flex gap-2">
          <Button variant="link" className="p-0" style={{ color: '#0d6efd' }} onClick={addBudgetRow} title="Add row">
            <i className="bi bi-plus-circle-fill" style={{ fontSize: 18 }}></i>
          </Button>
          <Button variant="link" className="p-0" style={{ color: '#d32f2f' }} onClick={removeBudgetRow} title="Remove last row" disabled={budgetRows.length <= 3}>
            <i className="bi bi-dash-circle-fill" style={{ fontSize: 18 }}></i>
          </Button>
        </div>
      </div>
      <div className="table-responsive">
        <Table striped bordered hover size="sm">
          <thead>
            <tr>
              <th style={{ width: '15%', fontWeight: 'normal' }}>Type of Request</th>
              <th style={{ width: '25%', fontWeight: 'normal' }}>Details of Request</th>
              <th style={{ width: '15%', fontWeight: 'normal' }}>Amount Requested</th>
              <th style={{ width: '15%', fontWeight: 'normal' }}>Actual Amount</th>
              <th style={{ width: '10%', fontWeight: 'normal' }}>Budget Utilized (%)</th>
              <th style={{ width: '25%', fontWeight: 'normal' }}>Remarks / Challenges</th>
            </tr>
          </thead>
          <tbody>
            {budgetRows.map((row) => (
              <tr key={row.id}>
                <td>{row.type || '-'}</td>
                <td style={{ padding: '8px', border: '1px solid #dee2e6' }}>
                  <Form.Control
                    as="textarea"
                    rows={2}
                    value={row.details}
                    onChange={(e) => handleBudgetChange(row.id, 'details', e.target.value)}
                    placeholder="Details of request"
                    style={{ border: '1px solid #ced4da', padding: '8px', fontSize: '0.875rem' }}
                  />
                </td>
                <td style={{ padding: '8px', border: '1px solid #dee2e6' }}>
                  <Form.Control
                    type="number"
                    value={row.amountRequested}
                    onChange={(e) => handleBudgetChange(row.id, 'amountRequested', e.target.value)}
                    placeholder="Amount"
                    style={{ border: '1px solid #ced4da', padding: '8px' }}
                  />
                </td>
                <td style={{ padding: '8px', border: '1px solid #dee2e6' }}>
                  <Form.Control
                    type="number"
                    value={row.actualAmount}
                    onChange={(e) => handleBudgetChange(row.id, 'actualAmount', e.target.value)}
                    placeholder="Actual"
                    style={{ border: '1px solid #ced4da', padding: '8px' }}
                  />
                </td>
                <td style={{ padding: '8px', border: '1px solid #dee2e6' }}>
                  <Form.Control
                    type="number"
                    value={row.budgetUtilized}
                    onChange={(e) => handleBudgetChange(row.id, 'budgetUtilized', e.target.value)}
                    placeholder="%"
                    min="0"
                    max="100"
                    style={{ border: '1px solid #ced4da', padding: '8px' }}
                  />
                </td>
                <td style={{ padding: '8px', border: '1px solid #dee2e6' }}>
                  <Form.Control
                    as="textarea"
                    rows={2}
                    value={row.remarks}
                    onChange={(e) => handleBudgetChange(row.id, 'remarks', e.target.value)}
                    placeholder="Remarks"
                    style={{ border: '1px solid #ced4da', padding: '8px', fontSize: '0.875rem' }}
                  />
                </td>
              </tr>
            ))}
            {/* Total Row */}
            <tr>
              <td><strong>Total</strong></td>
              <td></td>
              <td style={{ padding: '8px', border: '1px solid #dee2e6' }}>
                <Form.Control
                  type="number"
                  value={budgetTotals.totalAmountRequested}
                  onChange={(e) => handleBudgetTotalsChange('totalAmountRequested', e.target.value)}
                  placeholder="Enter total amount requested"
                  style={{ border: '1px solid #ced4da', padding: '8px', fontWeight: 'bold' }}
                />
              </td>
              <td style={{ padding: '8px', border: '1px solid #dee2e6' }}>
                <Form.Control
                  type="number"
                  value={budgetTotals.totalActualAmount}
                  onChange={(e) => handleBudgetTotalsChange('totalActualAmount', e.target.value)}
                  placeholder="Enter total actual amount"
                  style={{ border: '1px solid #ced4da', padding: '8px', fontWeight: 'bold' }}
                />
              </td>
              <td style={{ padding: '8px', border: '1px solid #dee2e6' }}>
                <Form.Control
                  type="number"
                  value={budgetTotals.totalBudgetUtilized}
                  onChange={(e) => handleBudgetTotalsChange('totalBudgetUtilized', e.target.value)}
                  placeholder="Enter total budget utilized"
                  style={{ border: '1px solid #ced4da', padding: '8px', fontWeight: 'bold' }}
                />
              </td>
              <td></td>
            </tr>
          </tbody>
        </Table>
      </div>
    </div>
  );

  return (
    <>
      <Modal 
        show={show} 
        onHide={handleClose} 
        size="xl" 
        centered 
        scrollable 
        backdrop="static" 
        keyboard={false}
      >
        <Modal.Header closeButton>
          <Modal.Title>
            <i className="bi bi-clipboard-data me-2"></i>
            {isEditMode ? 'Edit Monitoring Tool' : 'Create Monitoring Tool'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ padding: 0, pointerEvents: 'auto', position: 'relative' }}>
          {notification.show && (
            <Alert variant={notification.variant} dismissible onClose={dismiss} className="m-3 mb-0">
              {notification.message}
            </Alert>
          )}
          
          {loading ? (
            <div className="text-center py-5">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
              <div className="mt-2">Loading data...</div>
            </div>
          ) : (
            <Tab.Container activeKey={activeTab} onSelect={(k) => setActiveTab(k)}>
              <Nav variant="tabs" className="px-3 pt-3">
                <Nav.Item>
                  <Nav.Link eventKey="project">
                    <i className="bi bi-folder me-1 text-danger"></i>
                    Project & Details
                  </Nav.Link>
                </Nav.Item>
                <Nav.Item>
                  <Nav.Link 
                    eventKey="workplan"
                    disabled={!selectedProject && !isEditMode}
                  >
                    <i className="bi bi-clipboard-check me-1 text-danger"></i>
                    Work Plan
                  </Nav.Link>
                </Nav.Item>
                <Nav.Item>
                  <Nav.Link 
                    eventKey="budget"
                    disabled={!selectedProject && !isEditMode}
                  >
                    <i className="bi bi-cash-coin me-1 text-danger"></i>
                    Budget Utilization
                  </Nav.Link>
                </Nav.Item>
                <Nav.Item>
                  <Nav.Link
                    eventKey="signatories"
                    disabled={!selectedProject && !isEditMode}
                  >
                    <i className="bi bi-pen me-1 text-danger"></i>
                    Signatories
                  </Nav.Link>
                </Nav.Item>
              </Nav>

              <Tab.Content style={{ position: 'relative', pointerEvents: 'auto', minWidth: '100%', width: '100%' }}>
                <Tab.Pane eventKey="project" style={{ position: 'relative', pointerEvents: 'auto', minWidth: '100%', width: '100%', padding: '1rem' }}>
                  {renderProjectTab()}
                </Tab.Pane>
                <Tab.Pane eventKey="workplan" style={{ position: 'relative', pointerEvents: 'auto', minWidth: '100%', width: '100%', padding: '1rem' }}>
                  {renderWorkPlanTab()}
                </Tab.Pane>
                <Tab.Pane eventKey="budget" style={{ position: 'relative', pointerEvents: 'auto', minWidth: '100%', width: '100%', padding: '1rem' }}>
                  {renderBudgetTab()}
                </Tab.Pane>
                <Tab.Pane eventKey="signatories" style={{ position: 'relative', pointerEvents: 'auto', minWidth: '100%', width: '100%', padding: '1rem' }}>
                  {renderSignatoriesTab()}
                </Tab.Pane>
              </Tab.Content>
            </Tab.Container>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleClose}>
            Cancel
          </Button>
          {activeTab !== 'project' && (
            <Button variant="outline-primary" onClick={handlePreviousTab}>
              ← Previous
            </Button>
          )}
          {activeTab !== 'signatories' && (selectedProject || isEditMode) && (
            <Button variant="primary" onClick={handleNextTab}>
              Next →
            </Button>
          )}
          {activeTab === 'signatories' && (selectedProject || isEditMode) && (
            <Button variant="success" onClick={handleSubmit} disabled={submitting}>
              {submitting ? (isEditMode ? 'Updating...' : 'Submitting...') : 'Submit'}
            </Button>
          )}
        </Modal.Footer>
      </Modal>

      {/* Success Modal */}
      <Modal show={showSuccessModal} onHide={() => {
        setShowSuccessModal(false);
        handleClose();
      }} centered>
        <Modal.Header closeButton>
          <Modal.Title>Success</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="text-center py-3">
            <h5 className="mb-0">
              Monitoring Tool {wasNewMonitoring ? 'submitted' : 'updated'} successfully.
            </h5>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="success" onClick={() => {
            setShowSuccessModal(false);
            handleClose();
          }}>
            OK
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default MonitoringToolModal;

